import type { HTMLAttributes, ReactNode } from "react";
import {
  IgCodeEditor,
  type IgCodeEditorProps,
} from "@intelligems/ig-theme/IgCodeEditor";
import { cn } from "@src/lib/utils";
import { smartJsonStringify } from "@intelligems/ig-utils/smartJsonParse";

export type CodeBlockProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  code: unknown;
  language: string;
  showLineNumbers?: boolean;
  title?: ReactNode;
  children?: ReactNode;
};

const codeBlockLanguages = new Set<IgCodeEditorProps["language"]>([
  "html",
  "css",
  "js",
  "ts",
  "json",
  "md",
]);

const getCodeBlockLanguage = (
  language: string
): IgCodeEditorProps["language"] =>
  codeBlockLanguages.has(language as IgCodeEditorProps["language"])
    ? (language as IgCodeEditorProps["language"])
    : "json";

export const CodeBlock = ({
  code,
  language,
  showLineNumbers = false,
  className,
  children,
  title,
  ...props
}: CodeBlockProps) => {
  const formattedCode = smartJsonStringify(code);

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-md border bg-background text-foreground",
        className
      )}
      {...props}
    >
      <div className="relative">
        <IgCodeEditor
          title={title}
          copy={true}
          fontSize={"12px"}
          basicSetup={{ lineNumbers: showLineNumbers }}
          language={getCodeBlockLanguage(language)}
          readOnly
          maxHeight={"200px"}
          value={formattedCode}
        />
        {children ? (
          <div className="absolute top-2 right-2 flex items-center gap-2">
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
};
